/**
 * Authentication helper functions for consuming the shared oauth2 'AuthClient' class
 */
import {
  AuthClient,
  AuthConstants,
  AppLogger,
  DatadogClient,
  Localisation,
} from '@workivate/tho-web-shared';
import { getToken } from 'state/session/helpers';
import { getStore } from 'hermes-redux';
import { storageKeys } from 'globals/storage';
import { logout } from 'state/session/actions';
import { isUnifiedLoginEnabled } from 'globals/testable';
import Language from 'language';
import { getStoredSelectedEnvironment } from 'react-components/env-controller/env-controller-context';

export const getOauth2Lang = (language = '') => {
  // construct the redirect URL with the language parameter for USL to return to Zeus with the correct language
  if (Language.getLoginLanguagePersistence() && language) {
    language = Localisation.normaliseLocale(language);
  }
  return language;
};

/**
 * Gets the authentication header for the current user
 * Can be Wam-Token or Bearer (JWT)
 * If the smartRefresh fails, it will log the user out
 */
// AuthHeader can be an empty object OR must contain either Authorization or Wam-Token
type AuthHeader =
  | Record<string, never>
  | { Authorization: string }
  | { 'Wam-Token': string };

export const getAuthenticationHeader = async (): Promise<AuthHeader> => {
  const header = {};

  if (
    localStorage.getItem(storageKeys.authenticationMethod) ===
      AuthConstants.AUTH_METHODS.oauth2 &&
    isUnifiedLoginEnabled()
  ) {
    try {
      // We need to check with every call how close we are to the token expiration
      // see REFRESH_TOKEN_BUFFER in authClient.ts
      const idToken = await AuthClient.smartRefresh();

      if (idToken) {
        const jwtToken = idToken.getJwtString();
        header['Authorization'] = `Bearer ${jwtToken}`;
      }
    } catch (getJwtTokenError) {
      AppLogger.error('error in getAuthenticationHeader', getJwtTokenError);
      await getStore().dispatch(logout(false));
    }
  } else {
    const wamToken = getToken();
    if (wamToken) {
      header['Wam-Token'] = wamToken;
    }
  }

  return header;
};

/**
 * This function sets the clientId to the global context of DatadogClient
 * This means that the logs will be associated with the clientId
 * @param clientId
 */
export const setClientIdToGlobalContext = (clientId: string) => {
  DatadogClient.setGlobalContext({
    public_client_id: clientId,
  });
};

export const appendTestEnvironmentConfigParams = ({
  queryParams,
  configKey,
  configValue = localStorage.getItem(configKey),
}: {
  queryParams: URLSearchParams | string[];
  configKey: string;
  configValue?: string | null;
}): void => {
  if (configValue !== null && configValue !== undefined) {
    if (queryParams instanceof URLSearchParams) {
      queryParams.append(configKey, configValue);
    } else if (Array.isArray(queryParams)) {
      queryParams.push(`${configKey}=${configValue}`);
    }
  }
};

/**
 * Appends test environment configuration parameters to query params.
 * These are configuration parameters that can be turned on/off only in test environments.
 * It's important to pass these along when navigating to a different subdomain
 */
export const appendAllTestEnvironmentConfigParams = ({
  queryParams,
}: {
  queryParams: URLSearchParams | string[];
}): void => {
  if (!IS_PRODUCTION) {
    const storedEnvironment = getStoredSelectedEnvironment();

    // Append debug configuration parameters
    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'isUnifiedLoginEnabled',
    });
    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'dataDogEnabled',
    });

    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'debugLogsOn',
    });
    appendTestEnvironmentConfigParams({
      queryParams,
      configKey: 'setEnvironment',
      configValue: storedEnvironment,
    });

    // Append enabled log features
    const enabledLogFeatures = localStorage.getItem('enabledLogFeatures');
    if (enabledLogFeatures) {
      try {
        const enabledLogFeaturesArray = JSON.parse(enabledLogFeatures);
        if (queryParams instanceof URLSearchParams) {
          queryParams.append(
            'enabledLogFeatures',
            JSON.stringify(enabledLogFeaturesArray),
          );
        } else if (Array.isArray(queryParams)) {
          queryParams.push(
            `enabledLogFeatures=${JSON.stringify(enabledLogFeaturesArray)}`,
          );
        }
      } catch (error) {
        // Handle JSON parse error gracefully - don't append invalid data
        console.warn(
          'Failed to parse enabledLogFeatures from localStorage:',
          error,
        );
      }
    }
  }
};
